import ApiService from "./api.service";
import { getCurrentIdToken } from "../firebase/firebaseClient";

/**
 * Rider location lookup — the same v3 routes the user app uses before a
 * quote (see qa-api-tools/socket-tester/src/hailing-api.ts):
 * - GET  /me/locations/v3/place-autocomplete (search as you type)
 * - POST /me/locations/v3/place-details      (resolve a pick to coords)
 * - GET  /me/locations/v3/reverse-geocode    ("use current location" prefill)
 * Provider is GOOGLE for DASH. Every call needs a live Firebase ID token
 * (per-request headers, same as MeFlightAlertsService — getCurrentIdToken is
 * async and may refresh).
 */

export interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

export interface PickupPointOption {
  placeId: string;
  displayName: string;
  formattedAddress: string;
  lat: number | null;
  lng: number | null;
}

export interface ResolvedPlace {
  placeId: string;
  displayName: string;
  formattedAddress: string;
  lat: number | null;
  lng: number | null;
  /** The place's official pick-up points (mall, airport), when it has any. */
  pickupPoints?: PickupPointOption[];
}

/** `pickupPointPolygon.pickupPoints` of one place-details entry. */
const parsePickupPoints = (entry: Record<string, unknown>): PickupPointOption[] => {
  const polygon = entry.pickupPointPolygon;
  if (!polygon || typeof polygon !== "object") return [];
  const rows = (polygon as Record<string, unknown>).pickupPoints;
  if (!Array.isArray(rows)) return [];
  const points: PickupPointOption[] = [];
  for (const item of rows) {
    if (!item || typeof item !== "object") continue;
    const r = item as Record<string, unknown>;
    const pid = typeof r.placeId === "string" ? r.placeId : "";
    if (!pid) continue;
    const i18n = (typeof r.i18n === "object" && r.i18n !== null ? r.i18n : null) as Record<string, unknown> | null;
    const text = (key: string): string => {
      for (const lang of ["en", "zhHK"]) {
        const slot = i18n?.[lang];
        if (!slot || typeof slot !== "object") continue;
        const v = (slot as Record<string, unknown>)[key];
        if (typeof v === "string" && v.trim() && v.trim() !== "nan") return v.trim();
      }
      return "";
    };
    points.push({
      placeId: pid,
      displayName: text("displayName") || pid,
      formattedAddress: text("formattedAddress") || text("displayName") || pid,
      lat: typeof r.lat === "number" && Number.isFinite(r.lat) ? r.lat : null,
      lng: typeof r.lng === "number" && Number.isFinite(r.lng) ? r.lng : null,
    });
  }
  return points;
};

export class MeLocationService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const idToken = await getCurrentIdToken();
    return idToken ? { Authorization: `Bearer ${idToken}` } : {};
  }

  private language(): string {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const i18n = require("../i18n").default;
      return typeof i18n?.language === "string" && i18n.language.startsWith("zh")
        ? "zh-HK"
        : "en";
    } catch {
      return "en";
    }
  }

  async placeAutocomplete(args: {
    query: string;
    index: 0 | 1;
    lat?: number;
    lng?: number;
  }): Promise<PlaceSuggestion[]> {
    const headers = await this.authHeaders();
    const params: Record<string, string | number> = {
      provider: "GOOGLE",
      language: this.language(),
      query: args.query,
      index: args.index,
    };
    // lat/lng must both be provided or both omitted (backend zod refine).
    if (args.lat != null && args.lng != null) {
      params.lat = args.lat;
      params.lng = args.lng;
    }
    const { data } = await this.get<unknown>("/me/locations/v3/place-autocomplete", {
      params,
      headers,
    });
    if (!Array.isArray(data)) return [];
    const rows: PlaceSuggestion[] = [];
    for (const item of data) {
      if (!item || typeof item !== "object") continue;
      const r = item as Record<string, unknown>;
      const placeId = typeof r.placeId === "string" ? r.placeId : "";
      if (!placeId) continue;
      rows.push({
        placeId,
        mainText: typeof r.mainText === "string" && r.mainText ? r.mainText : placeId,
        secondaryText: typeof r.secondaryText === "string" ? r.secondaryText : "",
      });
    }
    return rows;
  }

  async placeDetails(placeIds: string[]): Promise<ResolvedPlace | null> {
    if (placeIds.length === 0) return null;
    const headers = await this.authHeaders();
    const { data } = await this.post<unknown>("/me/locations/v3/place-details", {
      provider: "GOOGLE",
      language: this.language(),
      placeIds,
    }, { headers });
    const first = Array.isArray(data) ? data[0] : data;
    if (!first || typeof first !== "object") return null;
    const r = first as Record<string, unknown>;
    const placeId = typeof r.placeId === "string" ? r.placeId : "";
    if (!placeId) return null;
    const pickupPoints = parsePickupPoints(r);
    return {
      placeId,
      displayName:
        (typeof r.displayName === "string" && r.displayName) || placeId,
      formattedAddress:
        (typeof r.formattedAddress === "string" && r.formattedAddress) ||
        (typeof r.displayName === "string" && r.displayName) ||
        placeId,
      lat: typeof r.lat === "number" && Number.isFinite(r.lat) ? r.lat : null,
      lng: typeof r.lng === "number" && Number.isFinite(r.lng) ? r.lng : null,
      ...(pickupPoints.length > 0 ? { pickupPoints } : {}),
    };
  }

  /** Pickup-point options inside a managed polygon (mall/hotel forecourt). */
  async pickupPointOptions(placeId: string): Promise<PickupPointOption[]> {
    const headers = await this.authHeaders();
    const { data } = await this.post<unknown>("/me/locations/v3/place-details", {
      provider: "GOOGLE",
      language: this.language(),
      placeIds: [placeId],
    }, { headers });
    const first = Array.isArray(data) ? data[0] : data;
    if (!first || typeof first !== "object") return [];
    return parsePickupPoints(first as Record<string, unknown>);
  }

  /**
   * Coords → place (pickup "use current location"). Follows the tester
   * (place-search.tsx): reverse-geocode returns a placeId, then place-details
   * resolves it to a full ResolvedPlace.
   */
  async reverseGeocode(args: { lat: number; lng: number; index: 0 | 1 }): Promise<ResolvedPlace | null> {
    const headers = await this.authHeaders();
    const { data } = await this.get<unknown>("/me/locations/v3/reverse-geocode", {
      params: {
        provider: "GOOGLE",
        language: this.language(),
        index: args.index,
        lat: args.lat,
        lng: args.lng,
      },
      headers,
    });
    const rec = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
    const place = (rec?.place && typeof rec.place === "object" ? rec.place : null) as Record<string, unknown> | null;
    const placeId = place && typeof place.placeId === "string" ? place.placeId : "";
    if (!placeId) return null;
    return this.placeDetails([placeId]);
  }
}
